import { Node, nodeInputRule } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { Plugin } from '@tiptap/pm/state'
import { timelineToDomSpec } from '@/lib/timeline'
import TimelineNodeView from '@/components/tiptap/TimelineNodeView'

const STARTER_SOURCE = [
  '## ERA LABEL | OPTIONAL SPAN',
  '1937 | Describe what happened in this year.',
  '1946 | Add another moment to the timeline.',
].join('\n')

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    timeline: {
      /** Insert a starter timeline node (used by the toolbar button). */
      insertTimeline: () => ReturnType
    }
  }
}

// Pulls the inner block out when the whole pasted plain text is a single
// `:::timeline … :::` fence. Mixed-content pastes should fall through to
// TipTap's default paste handling so surrounding content is not dropped.
function extractTimelineBlock(text: string): string | null {
  const match = text.match(/^\s*:::timeline[ \t]*\r?\n([\s\S]*?)\r?\n:::\s*$/i)
  if (!match) return null
  const inner = match[1].trim()
  return inner.length > 0 ? inner : null
}

export const Timeline = Node.create({
  name: 'timeline',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      source: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-timeline-source') || '',
        // `source` is serialized into the canonical structure by renderHTML;
        // returning {} here avoids a duplicate data attribute.
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'section[data-timeline-source]',
        getAttrs: (element) => ({
          source: (element as HTMLElement).getAttribute('data-timeline-source') || '',
        }),
      },
    ]
  },

  renderHTML({ node }) {
    return timelineToDomSpec(node.attrs.source as string)
  },

  addNodeView() {
    return ReactNodeViewRenderer(TimelineNodeView)
  },

  addCommands() {
    return {
      insertTimeline:
        () =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { source: STARTER_SOURCE } }),
    }
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /^:::timeline[ \t]*$/,
        type: this.type,
        getAttributes: () => ({ source: '' }),
      }),
    ]
  },

  addProseMirrorPlugins() {
    const type = this.type
    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            const text = event.clipboardData?.getData('text/plain')
            if (!text) return false
            const inner = extractTimelineBlock(text)
            if (!inner) return false

            const node = type.create({ source: inner })
            const tr = view.state.tr.replaceSelectionWith(node)
            view.dispatch(tr.scrollIntoView())
            return true
          },
        },
      }),
    ]
  },
})

export default Timeline
